import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from "@angular/core";
import { SHARED_ENVIRONMENT } from '@k12/environment';
import {
  EmployeeDataModel,
  EmployeeSchoolOption,
  EmployeeSubmitPayload,
  EmployeeSummary,
  ProviderEnrollmentStatusModel,
  ProviderInfoStatus,
  SubService,
  ProviderData,
  ProviderTypeId,
  toProviderTypeId
} from '@k12/models';
import { Observable } from 'rxjs';

export interface PagePayload {
  filter: {
    pageNumber?: number;
    pageSize?: number;
    orderBy?: string;
    orderDirection?: number;
    searchTerm?: string;
  }
}

const DEFAULT_VALIDATED_STATUS = 2;

@Injectable({
  providedIn: 'root'
})

export class ProviderService {
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;
  private readonly http = inject(HttpClient);

  deleteEmployee(employeeId: string): Observable<void> {
    return this.http.delete<void>(this.apiUrl + "/partners/employees/" + employeeId);
  }

  getAllEmployeesSummaryAdmin(programPartnerId: string, payload?: PagePayload): Observable<EmployeeSummary[]> {
    return this.http.post<EmployeeSummary[]>(`${this.apiUrl}/partners/${programPartnerId}/employees/summary`, payload);
  }

  getAllEmployeesSummary(): Observable<EmployeeSummary[]> {
    return this.http.get<EmployeeSummary[]>(`${this.apiUrl}/partners/employees/summary`);
  }

  getSubServices(employeeType?: number): Observable<SubService[]> {
    const url = (employeeType) ? `partners/employees/subservices?employeeType=${employeeType}` : `partners/employees/subservices`;
    return this.http.get<SubService[]>(`${this.apiUrl}/${url}`);
  }

  /** NSC degree-institution catalog (~6,000 rows), reused from the Add/Edit Employee school picker. */
  getSchools(): Observable<EmployeeSchoolOption[]> {
    return this.http.get<EmployeeSchoolOption[]>(`${this.apiUrl}/partners/schools`);
  }

  setEnrollmentStatus(status: ProviderInfoStatus | string): ProviderInfoStatus {
    return status === ProviderInfoStatus.Review || status === 'UnderReview'
      ? ProviderInfoStatus.UnderReview
      : status as ProviderInfoStatus;
  }

  getEmployeeSubServices(type: number): Observable<SubService[]> {
    return this.http.get<SubService[]>(
      `${this.apiUrl}/partners/employees/subservices?employeeType=${type}`
    );
  }

  updateEmployee(payload: EmployeeSubmitPayload): Observable<EmployeeDataModel> {
    return this.http.put<EmployeeDataModel>(
      `${this.apiUrl}/partners/employees/${payload.id}`,
      payload
    );
  }

  getProviderEnrollmentStatuses(): Observable<ProviderEnrollmentStatusModel[]> {
    return this.http.get<ProviderEnrollmentStatusModel[]>(
      `${this.apiUrl}/partners/providerenrollmentstatuses`
    );
  }

  buildEmployeeUpdatePayload(userData: ProviderData): EmployeeSubmitPayload {
    const ownerDetails = userData.ownerDetails;
    const providerTypeId =
      userData.providerTypeId
      ?? toProviderTypeId(userData.type ?? userData.providerType)
      ?? ownerDetails?.employeeType
      ?? null;

    const ownerDetailsRecord: Record<string, unknown> =
      ownerDetails && typeof ownerDetails === 'object'
        ? (ownerDetails as Record<string, unknown>)
        : {};

    const ownerPersonalAddress =
      ownerDetails?.personalAddress && typeof ownerDetails.personalAddress !== 'string'
        ? ownerDetails.personalAddress
        : undefined;

    const normalizedSubServices = Array.isArray(ownerDetails?.subServices) ? ownerDetails.subServices : [];
    const birthDate = this.normalizeDateFormat(ownerDetails?.birthDate) || null;

    return {
      id: ownerDetails?.id,
      personId: ownerDetails?.personId,
      employeeType: providerTypeId,
      firstName: ownerDetails?.firstName ?? '',
      lastName: ownerDetails?.lastName ?? '',
      phone: ownerDetails?.phone ?? null,
      secondaryPhone: ownerDetails?.secondaryPhone ?? null,
      birthDate,
      email: ownerDetails?.email ?? null,
      validatedStatus: DEFAULT_VALIDATED_STATUS,
      ssn: ownerDetails?.ssn ?? userData.ssn ?? null,
      subServices: normalizedSubServices.map((subService) => ({
        id: subService.id ?? null,
        name: subService.name ?? '',
        description: subService.description ?? ''
      })),
      isOwner: true,
      personalAddress: ownerPersonalAddress,
      programPartnerId: userData.id ?? ownerDetails?.programPartnerId ?? '',
      ...(providerTypeId === ProviderTypeId.Tutor
        ? {
          graduationDate: this.normalizeDateFormat(ownerDetails?.graduationDate) || undefined,
          schoolId: ownerDetails?.schoolId ?? null,
          schoolCode: ownerDetails?.schoolCode ?? null,
          schoolName: ownerDetails?.schoolName ?? null,
          informationCertified: this.toBooleanFlag(ownerDetailsRecord['informationCertified']),
          credentialReleaseConsent: this.toBooleanFlag(ownerDetailsRecord['credentialReleaseConsent'])
        }
        : {}),
      ...(providerTypeId === ProviderTypeId.Therapy || providerTypeId === ProviderTypeId.Transportation
        ? {
          licenseNumber: ownerDetails?.licenseNumber ?? null,
          issuingState: this.resolveIssuingState(ownerDetails?.issuingState)
        }
        : {}),
      ...(providerTypeId === ProviderTypeId.Therapy
        ? {
          npiNumber: ownerDetails?.npiNumber ?? null,
          npiNumber2: ownerDetails?.npiNumber2 ?? null,
          therapyLicenseTypeId: ownerDetails?.therapyLicenseTypeId
        }
        : {}),
      ...(providerTypeId === ProviderTypeId.Transportation
        ? {
          transportationLicenseTypeId: ownerDetails?.transportationLicenseTypeId ?? null
        }
        : {})
    };
  }

  private normalizeDateFormat(date: string | Date | null | undefined): string {
    if (!date) return '';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().split('T')[0];
  }

  private toBooleanFlag(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return false;
  }

  private resolveIssuingState(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'code' in value) {
      const code = (value as { code?: unknown }).code;
      return typeof code === 'string' ? code : '';
    }
    return '';
  }
}
